import {
  LineChartIcon as ChartLineIcon,
  CompassIcon,
  FileText,
  Folder,
  KanbanIcon,
  LightbulbIcon,
  List,
  type LucideIcon,
  NewspaperIcon,
  PanelsTopLeftIcon,
  Rocket,
  SettingsIcon,
  ShieldAlertIcon,
  TargetIcon,
  TrophyIcon,
  TvMinimalPlay,
  UsersRoundIcon,
  WorkflowIcon,
} from 'lucide-react'

import { YoutubeIcon, YoutubeWhiteIcon } from '@/components/icons'
import { Actors } from '@/types/actors'

import { generateRoutePath } from './generateRoutePath'

import type { Route } from './types'

type Item = {
  icon: LucideIcon
  label: string
  link: string
  allowedBy: Actors[]
  disabled: boolean
  matchPattern: string
  tooltipText?: string
  allowedEmails?: string[]
  subItems?: Item[]
}

export type MenuItem = {
  key: string
  icon: LucideIcon
  label: string
  matchPattern: string
  allowedBy?: Actors[]
  items: {
    icon: LucideIcon | null
    label: string
    link: string
    allowedBy: Actors[]
    disabled: boolean
    matchPattern: string
    tooltipText?: string
    allowedEmails?: string[]
    subItems?: Item[]
  }[]
}

export const MENU_MODULES: MenuItem[] = [
  {
    key: 'ideas',
    icon: LightbulbIcon,
    label: 'Iniciativas',
    matchPattern: '^/idea-management(/.*)?$',
    allowedBy: [Actors.IDEATOR, Actors.MANAGER],
    items: [
      {
        icon: CompassIcon,
        label: 'Panorama',
        link: generateRoutePath('ideaManagement.panorama'),
        allowedBy: [Actors.MANAGER],
        disabled: false,
        matchPattern: '^/idea-management/?$',
        subItems: [],
      },
      {
        icon: TargetIcon,
        label: 'Direcionais ativos',
        link: generateRoutePath('ideaManagement.publishedChallenges'),
        allowedBy: [Actors.IDEATOR, Actors.MANAGER],
        disabled: false,
        matchPattern: '^/idea-management/published-challenges(/.*)?$',
        subItems: [],
      },
      {
        icon: WorkflowIcon,
        label: 'Gestão de direcionais',
        link: generateRoutePath('ideaManagement.challengeManagement'),
        allowedBy: [Actors.MANAGER],
        disabled: false,
        matchPattern: '^/idea-management/challenge-management(/.*)?$',
        subItems: [],
      },
      {
        icon: KanbanIcon,
        label: 'Funil de iniciativas',
        link: generateRoutePath('ideaManagement.kanban'),
        allowedBy: [Actors.MANAGER],
        disabled: false,
        matchPattern: '^/idea-management/kanban(/.*)?$',
        subItems: [],
      },
      {
        icon: List,
        label: 'Lista de iniciativas',
        link: generateRoutePath('ideaManagement.ideaList'),
        allowedBy: [Actors.MANAGER],
        disabled: false,
        matchPattern: '^/idea-management/idea-list(/.*)?$',
        subItems: [],
      },
      {
        icon: NewspaperIcon,
        label: 'Feed',
        link: generateRoutePath('ideaManagement.feed'),
        allowedBy: [Actors.IDEATOR, Actors.MANAGER],
        disabled: false,
        matchPattern: '^/idea-management/feed(/.*)?$',
        subItems: [],
      },
      {
        icon: TrophyIcon,
        label: 'Ranking',
        link: generateRoutePath('ideaManagement.ranking'),
        allowedBy: [Actors.IDEATOR, Actors.MANAGER],
        disabled: false,
        matchPattern: '^/idea-management/ranking(/.*)?$',
        subItems: [],
      },
      {
        icon: ChartLineIcon,
        label: 'Painel de dados',
        link: generateRoutePath('ideaManagement.dashboard'),
        allowedBy: [Actors.MANAGER],
        disabled: false,
        matchPattern: '^/idea-management/dashboard(/.*)?$',
        subItems: [],
      },
    ],
  },
  {
    key: 'open-innovation',
    icon: Rocket,
    label: 'Inovação Aberta',
    matchPattern: '^/open-innovation(/.*)?$',
    allowedBy: [Actors.MANAGER, Actors.IDEATOR],
    items: [
      {
        icon: CompassIcon,
        label: 'Base de startups',
        link: generateRoutePath('openInnovation.startups', {
          params: { tab: 'all' },
        }),
        allowedBy: [Actors.MANAGER, Actors.IDEATOR],
        disabled: false,
        matchPattern: '^/open-innovation/startups(/.*)?$',
        subItems: [],
      },
    ],
  },
  {
    key: 'projects',
    icon: Folder,
    label: 'Projetos',
    matchPattern: '^/projects(/.*)?$',
    allowedBy: [Actors.IDEATOR, Actors.MANAGER],
    items: [],
  },
]

type MenuExternalItem = {
  link: Route
  isAdmin: boolean
  allowedEmails?: string[]
} & MenuItem

export const MENU_EXTERNAL_ITEMS: MenuExternalItem[] = [
  {
    key: 'knowledgeTrail',
    icon: TvMinimalPlay,
    label: 'Trilha de conhecimento',
    link: 'knowledge-trail',
    matchPattern: '^/knowledge-trail(/.*)?$',
    isAdmin: false,
    items: [
      {
        icon: YoutubeIcon,
        label: 'Trilha de videos',
        link: generateRoutePath('knowledgeTrail.videos'),
        allowedBy: [Actors.IDEATOR, Actors.MANAGER],
        disabled: false,
        matchPattern: '^/knowledge-trail/videos(/.*)?$',
        subItems: [
          {
            icon: YoutubeWhiteIcon,
            label: 'Jornada de ideação',
            link: generateRoutePath('knowledgeTrail.videos.ideation'),
            allowedBy: [Actors.IDEATOR, Actors.MANAGER],
            disabled: false,
            matchPattern: '^/knowledge-trail/videos/ideation(/.*)?$',
          },
        ],
      },
      {
        icon: FileText,
        label: 'Artigos',
        link: generateRoutePath('knowledgeTrail.articles'),
        allowedBy: [Actors.IDEATOR, Actors.MANAGER],
        disabled: false,
        matchPattern: '^/knowledge-trail/articles(/.*)?$',
        subItems: [],
      },
    ],
  },
  {
    key: 'adminPortal',
    icon: SettingsIcon,
    label: 'Portal do administrador',
    matchPattern: '^/admin-portal(/.*)?$',
    link: 'adminPortal.userManagement',
    items: [
      {
        icon: UsersRoundIcon,
        label: 'Gestão de usuários',
        link: generateRoutePath('adminPortal.userManagement'),
        allowedBy: [Actors.MANAGER],
        disabled: false,
        matchPattern: '^/admin-portal/user-management(/.*)?$',
        subItems: [],
      },
      {
        icon: SettingsIcon,
        label: 'Geral',
        link: generateRoutePath('adminPortal.geral'),
        allowedBy: [Actors.MANAGER],
        disabled: false,
        matchPattern: '^/admin-portal/geral(/.*)?$',
        subItems: [],
      },
      {
        icon: PanelsTopLeftIcon,
        label: 'Templates',
        link: generateRoutePath('adminPortal.template'),
        allowedBy: [Actors.MANAGER],
        disabled: false,
        matchPattern: '^/admin-portal/template(/.*)?$',
        subItems: [],
      },
      {
        icon: ShieldAlertIcon,
        label: 'Administração Avantti',
        link: generateRoutePath('adminPortal.avantti'),
        allowedBy: [Actors.MANAGER],
        disabled: false,
        matchPattern: '^/admin-portal/avantti(/.*)?$',
        subItems: [],
        allowedEmails: [
          'vinicius.arantes@inventta.net',
          'jessica.hakime@inventta.net',
          'evelyn.monteiro@inventta.net',
          'patrick.louback@inventta.net',
          'pedro.venchiarutti@inventta.net',
        ],
      },
    ],
    isAdmin: true,
  },
]
